import type { MiniGamesConfig } from '../config/minigames.config';
import type { CraneGame } from '../minigames/CraneGame';
import type { HammerGame } from '../minigames/HammerGame';
import { MiniGameKind, MiniGamePhase, type MiniGame } from '../minigames/MiniGame';
import type { MiniGameSession } from '../minigames/MiniGameSession';
import type { MixerGame } from '../minigames/MixerGame';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';
import { CranePainter } from './minigames/cranePainter';
import { HammerStageView } from './minigames/HammerStageView';
import { drawHammerEffects } from './minigames/hammerEffects';
import { MixerPainter } from './minigames/mixerPainter';

const TEXT: Readonly<Record<MiniGameKind, { readonly title: string; readonly hint: string }>> = {
  [MiniGameKind.Hammer]: { title: 'HAMMER & NAGEL', hint: 'Tippen zum Schlagen – genau mittig geht der Nagel ganz rein' },
  [MiniGameKind.Crane]: { title: 'KRANFAHRT', hint: 'Tippen zum Ausklinken – die Last genau auf die Marke setzen' },
  [MiniGameKind.Mixer]: { title: 'BETONMISCHER', hint: 'Tippen zum Stoppen – die Nadel auf die goldene Linie' },
};

/**
 * Mini-game panel over the frozen picture (like the quiz panel): title, the bonus the share reached so far, the
 * game's canvas, attempt dots and the attempt's time bar, the result. A tap anywhere on it (or the action keys) is the
 * game's one action; nothing reaches the run meanwhile.
 */
export class MiniGameOverlay {
  readonly element: HTMLDivElement;
  private readonly title: HTMLDivElement;
  private readonly bonus: HTMLSpanElement;
  private readonly bonusMax: HTMLSpanElement;
  private readonly canvas: HTMLCanvasElement;
  /** 3D layer under the 2D canvas (hammer & nail; its own small engine, made on first use). */
  private readonly canvas3d: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;
  private readonly dots: HTMLDivElement;
  private readonly timeFill: HTMLDivElement;
  private readonly hint: HTMLDivElement;
  private readonly result: HTMLDivElement;
  private readonly hammer: HammerStageView;
  private readonly crane: CranePainter;
  private readonly mixer: MixerPainter;
  private shownGame: MiniGame | null = null;
  private resultShown = false;
  private suspended = false;
  private time = 0;

  constructor(
    root: HTMLElement,
    private readonly config: MiniGamesConfig,
    private readonly onTap: () => void,
    /** Bottles taken off when the game reached nothing (QuizConfig.wrongPenalty). */
    private readonly penalty: number,
    /** The game's sky (prefiltered .env) – the steel of the 3D models reflects it. */
    envUrl: string,
  ) {
    this.hammer = new HammerStageView(config.hammer, envUrl);
    this.crane = new CranePainter(config.crane);
    this.mixer = new MixerPainter(config.mixer);
    this.element = el('div', 'quiz minigame', undefined, root);
    this.element.setAttribute('role', 'dialog');
    this.element.hidden = true;
    const panel = el('div', 'quiz-panel minigame-panel', undefined, this.element);
    const head = el('div', 'quiz-head', undefined, panel);
    head.appendChild(icon(ICONS.collect, 'quiz-icon'));
    this.title = el('div', 'quiz-title', '', head);
    const bonusBox = el('div', 'minigame-bonus', undefined, head);
    this.bonus = el('span', 'minigame-bonus-value', '0', bonusBox);
    this.bonusMax = el('span', 'minigame-bonus-max', '', bonusBox);
    const stage = el('div', 'minigame-stage', undefined, panel);
    this.canvas3d = el('canvas', 'minigame-canvas minigame-canvas-3d', undefined, stage);
    this.canvas3d.hidden = true;
    this.canvas = el('canvas', 'minigame-canvas', undefined, stage);
    this.ctx = this.canvas.getContext('2d')!;
    const status = el('div', 'minigame-status', undefined, panel);
    this.dots = el('div', 'minigame-dots', undefined, status);
    const bar = el('div', 'quiz-time-bar', undefined, status);
    this.timeFill = el('div', 'quiz-time-fill', undefined, bar);
    this.hint = el('div', 'minigame-hint', '', panel);
    this.result = el('div', 'quiz-result', '', panel);
    this.result.setAttribute('aria-live', 'assertive');
    // pointerdown, not click: every hundredth of a second counts
    this.element.addEventListener('pointerdown', this.onPointer);
    window.addEventListener('keydown', this.onKey, { capture: true });
  }

  /** The game's picture is up (3D models loaded) – its clock may run. */
  isReady(kind: MiniGameKind): boolean {
    return kind !== MiniGameKind.Hammer || this.hammer.isReady;
  }

  /** Renders the session (every frame while it is active). */
  update(session: MiniGameSession, dt: number): void {
    const game = session.game;
    if (!session.active || !game) {
      this.hide();
      return;
    }
    this.time += dt;
    if (game !== this.shownGame) this.show(game, session);
    if (session.phase === MiniGamePhase.Playing) this.bonus.textContent = formatNumber(session.bonus);
    this.timeFill.style.transform = `scaleX(${game.timeShare})`;
    this.element.classList.toggle('is-hurry', game.timeShare < 0.35);
    this.renderDots(game);
    if (session.phase === MiniGamePhase.Result && !this.resultShown) this.showResult(session);
    this.paint(game);
  }

  setSuspended(suspended: boolean): void {
    this.suspended = suspended;
    this.element.classList.toggle('is-suspended', suspended);
  }

  hide(): void {
    if (this.element.hidden) return;
    this.element.hidden = true;
    this.shownGame = null;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey, { capture: true });
    this.element.remove();
  }

  private show(game: MiniGame, session: MiniGameSession): void {
    this.shownGame = game;
    this.resultShown = false;
    this.element.hidden = false;
    this.element.classList.remove('is-result', 'is-correct', 'is-wrong', 'is-hurry');
    this.element.setAttribute('aria-label', TEXT[game.kind].title);
    this.title.textContent = TEXT[game.kind].title;
    this.canvas3d.hidden = game.kind !== MiniGameKind.Hammer;
    if (game.kind === MiniGameKind.Hammer) void this.hammer.prepare(this.canvas3d).catch((error: unknown) => console.warn('[minigame] hammer set not loaded', error));
    this.hint.textContent = TEXT[game.kind].hint;
    this.bonusMax.textContent = `/ ${formatNumber(session.maxBonus)}`;
    this.result.textContent = '';
    this.dots.replaceChildren(...Array.from({ length: game.attemptsTotal }, () => el('span', 'minigame-dot')));
  }

  private renderDots(game: MiniGame): void {
    const dots = this.dots.children;
    for (let i = 0; i < dots.length; i++) dots[i]!.classList.toggle('is-used', i < game.attemptsMade);
  }

  private showResult(session: MiniGameSession): void {
    this.resultShown = true;
    const outcome = session.outcome;
    if (!outcome) return;
    const percent = Math.round(outcome.share * 100);
    this.element.classList.add('is-result', outcome.share > 0 ? 'is-correct' : 'is-wrong');
    this.hint.textContent = '';
    this.result.textContent =
      outcome.share >= 1 - 1e-9 ? `MEISTERHAFT! 100 % · +${formatNumber(outcome.bonus)} Flaschen` : outcome.bonus > 0 ? `${percent} % · +${formatNumber(outcome.bonus)} Flaschen` : `DANEBEN! −${formatNumber(this.penalty)} Flaschen`;
    if (outcome.bonus <= 0) this.bonus.textContent = `−${formatNumber(this.penalty)}`;
  }

  /** Canvas at the stage's size and the device's pixel ratio; the painters draw in CSS pixels. */
  private paint(game: MiniGame): void {
    const rect = this.canvas.getBoundingClientRect();
    const w = Math.max(1, Math.round(rect.width));
    const h = Math.max(1, Math.round(rect.height));
    const scale = Math.min(2, window.devicePixelRatio || 1);
    const pw = Math.round(w * scale);
    const ph = Math.round(h * scale);
    if (this.canvas.width !== pw || this.canvas.height !== ph) {
      this.canvas.width = pw;
      this.canvas.height = ph;
    }
    const ctx = this.ctx;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.clearRect(0, 0, w, h);
    switch (game.kind) {
      case MiniGameKind.Hammer:
        this.paintHammer(ctx, w, h, game as HammerGame);
        break;
      case MiniGameKind.Crane:
        this.crane.paint(ctx, w, h, game as CraneGame, this.time, scale);
        break;
      case MiniGameKind.Mixer:
        this.mixer.paint(ctx, w, h, game as MixerGame, this.time, scale);
        break;
    }
  }

  /** 3D scene below, sparks and the verdict on the transparent canvas above; a short note while the set loads. */
  private paintHammer(ctx: CanvasRenderingContext2D, w: number, h: number, game: HammerGame): void {
    const nail = this.hammer.render(game, this.time);
    if (!nail) {
      ctx.fillStyle = '#11182a';
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(255,255,255,0.8)';
      ctx.font = '600 16px "Segoe UI", Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Werkzeug wird geholt …', w / 2, h / 2);
      return;
    }
    drawHammerEffects(ctx, w, h, game, nail);
  }

  private readonly onPointer = (event: PointerEvent): void => {
    if (this.element.hidden || this.suspended) return;
    event.preventDefault();
    event.stopPropagation();
    this.onTap();
  };

  private readonly onKey = (event: KeyboardEvent): void => {
    if (this.element.hidden || this.suspended) return;
    // while the game is up, no key reaches the run (no lane change, no pause)
    event.preventDefault();
    event.stopPropagation();
    if (!event.repeat && this.config.actionKeys.some((k) => k.toLowerCase() === event.key.toLowerCase())) this.onTap();
  };
}
